/**
 * Procedural ENTRYWAY audio (Floorplan v2 / ADR-0022) — the doorbell chime + the door knock.
 *
 * The cousin of the soundboard ({@link import('./soundboard-audio')}): two short sounds SYNTHESIZED here
 * with the Web Audio API (oscillators + filtered noise + gentle envelopes) — NO binary asset files. The
 * wire only ever carries the one-shot event (BELL_RANG / KNOCKED); the synthesis lives entirely client-
 * side. Kept self-contained (its own lazily-created AudioContext) so it can never regress the soundboard.
 *
 * Sound Director constraints (so these are warm, never grief vectors):
 *   • SHORT — the bell is ~1.4s, the knock ~0.35s; a rapid burst can never become a wall of noise.
 *   • GENTLE — soft envelopes (no DC clicks), a modest master ceiling, capped concurrency, a debounce.
 *   • RESPECTS THE SYSTEM — a single shared context, resumed on a user gesture (autoplay policy), and
 *     never created during SSR / in a non-DOM environment. Failures are silent no-ops (audio is a bonus).
 */

/** Master ceiling so the bell/knock stay comfortable even alongside the soundboard. This is the channel
 *  CEILING (the "100%" point); the player's effects-volume preference scales below it via {@link setEntrywayVolume}. */
const MASTER_GAIN = 0.26;

/** The player's effects-volume preference as a 0..1 multiplier on {@link MASTER_GAIN} (1 = full). The
 *  entryway shares the ONE "Sound effects" fader with the soundboard, so the same level drives both. Kept
 *  at module scope so it survives the lazy (re)creation of this self-contained AudioContext. */
let sfxVolume = 1;
/** Most clips that can ring at once before extra triggers are dropped (no pile-ups). */
const MAX_CONCURRENT = 3;
/** Per-sound debounce (ms): coalesces an accidental double-fire of the same sound into one play. */
const DEBOUNCE_MS = 80;

type AudioContextCtor = new () => AudioContext;

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let voices = 0;
const lastPlayedAt = new Map<string, number>();

// ── Dev/e2e observability (read by OfficePage's `window.__duckEntry()` hook) ──
let lastPlayed: 'doorbell' | 'knock' | null = null;
let playedCount = 0;

function audioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

function ensureContext(): { ctx: AudioContext; master: GainNode } | null {
  if (ctx && master) return { ctx, master };
  const Ctor = audioContextCtor();
  if (!Ctor) return null;
  try {
    const created = new Ctor();
    const gain = created.createGain();
    gain.gain.value = MASTER_GAIN * sfxVolume;
    gain.connect(created.destination);
    ctx = created;
    master = gain;
    return { ctx, master };
  } catch {
    return null;
  }
}

/** Resume the AudioContext from a user gesture (autoplay policy). Safe to call repeatedly; a no-op otherwise. */
export function unlockEntrywayAudio(): void {
  const audio = ensureContext();
  if (audio && audio.ctx.state === 'suspended') void audio.ctx.resume();
}

/**
 * Set the effects channel volume — a 0..1 multiplier on {@link MASTER_GAIN} (0 = silent, 1 = full). The
 * entryway shares the one "Sound effects" fader with the soundboard, so the audio mix calls this with the
 * SAME level. Remembered at module scope (applies even before the context exists); ramps if already live.
 */
export function setEntrywayVolume(value: number): void {
  sfxVolume = Math.max(0, Math.min(1, value));
  if (master && ctx) {
    const target = MASTER_GAIN * sfxVolume;
    try {
      master.gain.setTargetAtTime(target, ctx.currentTime, 0.02);
    } catch {
      master.gain.value = target;
    }
  }
}

/** A soft attack→decay gain envelope, scheduled on the AudioContext clock (no DC clicks). */
function envelope(node: GainNode, start: number, peak: number, attack: number, duration: number): void {
  const g = node.gain;
  g.setValueAtTime(0.0001, start);
  g.exponentialRampToValueAtTime(Math.max(0.0002, peak), start + attack);
  g.exponentialRampToValueAtTime(0.0001, start + duration);
  g.setValueAtTime(0, start + duration + 0.01);
}

/** A bell strike: a fundamental sine + a quiet inharmonic partial, with a long bell-like decay. */
function bellNote(audioCtx: AudioContext, out: GainNode, freq: number, when: number, dur: number): void {
  [
    { mult: 1, level: 0.6 },
    { mult: 2.76, level: 0.18 }, // an inharmonic partial gives a metallic "bell" timbre
  ].forEach(({ mult, level }) => {
    const osc = audioCtx.createOscillator();
    const g = audioCtx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq * mult, when);
    envelope(g, when, level, 0.005, dur);
    osc.connect(g).connect(out);
    osc.start(when);
    osc.stop(when + dur + 0.05);
  });
}

/** A single knock: a short low thud — a fast sine click through a lowpass, plus a noise transient. */
function knockHit(audioCtx: AudioContext, out: GainNode, when: number): void {
  const osc = audioCtx.createOscillator();
  const g = audioCtx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(160, when);
  osc.frequency.exponentialRampToValueAtTime(70, when + 0.08);
  envelope(g, when, 0.7, 0.004, 0.12);
  osc.connect(g).connect(out);
  osc.start(when);
  osc.stop(when + 0.16);

  // A tiny filtered-noise transient for the "wood" attack.
  const len = Math.max(1, Math.floor(audioCtx.sampleRate * 0.04));
  const buf = audioCtx.createBuffer(1, len, audioCtx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i += 1) data[i] = Math.random() * 2 - 1;
  const src = audioCtx.createBufferSource();
  src.buffer = buf;
  const lp = audioCtx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 900;
  const ng = audioCtx.createGain();
  envelope(ng, when, 0.35, 0.001, 0.05);
  src.connect(lp).connect(ng).connect(out);
  src.start(when);
  src.stop(when + 0.08);
}

/** Shared gate: context ready + running + debounce + concurrency. Returns the per-clip out gain, or null. */
function beginClip(id: 'doorbell' | 'knock' | 'door'): { audioCtx: AudioContext; out: GainNode } | null {
  const audio = ensureContext();
  if (!audio) return null;
  const { ctx: audioCtx, master: bus } = audio;
  if (audioCtx.state === 'suspended') {
    void audioCtx.resume();
    return null; // skip THIS play rather than blast a delayed burst when it unlocks
  }
  const now = audioCtx.currentTime * 1000;
  const last = lastPlayedAt.get(id);
  if (typeof last === 'number' && now - last < DEBOUNCE_MS) return null;
  lastPlayedAt.set(id, now);
  if (voices >= MAX_CONCURRENT) return null;

  const out = audioCtx.createGain();
  out.gain.value = 1;
  out.connect(bus);
  voices += 1;
  return { audioCtx, out };
}

/** Release the concurrency slot + tear down the node a beat after the clip ends. */
function endClip(out: GainNode, durationSec: number): void {
  const releaseMs = Math.ceil((durationSec + 0.1) * 1000);
  window.setTimeout(() => {
    voices = Math.max(0, voices - 1);
    try {
      out.disconnect();
    } catch {
      /* already gone */
    }
  }, releaseMs);
}

/**
 * Play the doorbell — a friendly "ding-dong" (a descending major third on bell tones). Heard by EVERYONE
 * when a citizen rings the entry bell. Returns true if it actually started, false if gated.
 */
export function playDoorbell(): boolean {
  const clip = beginClip('doorbell');
  if (!clip) return false;
  const { audioCtx, out } = clip;
  const t0 = audioCtx.currentTime + 0.01;
  bellNote(audioCtx, out, 659.25, t0, 0.7); // "ding" — E5
  bellNote(audioCtx, out, 523.25, t0 + 0.34, 1.0); // "dong" — C5
  const durationSec = 1.4;
  lastPlayed = 'doorbell';
  playedCount += 1;
  endClip(out, durationSec);
  return true;
}

/**
 * Play the door knock — two soft low thuds. Heard only by ducks NEAR the door (the server picks the
 * recipients). Returns true if it actually started, false if gated.
 */
export function playKnock(): boolean {
  const clip = beginClip('knock');
  if (!clip) return false;
  const { audioCtx, out } = clip;
  const t0 = audioCtx.currentTime + 0.01;
  knockHit(audioCtx, out, t0);
  knockHit(audioCtx, out, t0 + 0.18);
  const durationSec = 0.4;
  lastPlayed = 'knock';
  playedCount += 1;
  endClip(out, durationSec);
  return true;
}

/**
 * Play an interior door OPEN/CLOSE sound (Room Doors) — a soft wooden latch click + a short pitch sweep
 * (lifting when opening, dropping when closing). Local feedback for the actor; the synced leaf swing is the
 * cue everyone else sees. Returns true if it actually started, false if gated.
 */
export function playDoorToggle(open: boolean): boolean {
  const clip = beginClip('door');
  if (!clip) return false;
  const { audioCtx, out } = clip;
  const t0 = audioCtx.currentTime + 0.01;
  // A short sine that lifts (open) or drops (close) in pitch — the "swing".
  const osc = audioCtx.createOscillator();
  const g = audioCtx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(open ? 210 : 190, t0);
  osc.frequency.exponentialRampToValueAtTime(open ? 320 : 95, t0 + 0.12);
  envelope(g, t0, 0.45, 0.004, 0.16);
  osc.connect(g).connect(out);
  osc.start(t0);
  osc.stop(t0 + 0.2);
  // A filtered-noise "wood" transient for the latch click.
  const len = Math.max(1, Math.floor(audioCtx.sampleRate * 0.05));
  const buf = audioCtx.createBuffer(1, len, audioCtx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i += 1) data[i] = Math.random() * 2 - 1;
  const src = audioCtx.createBufferSource();
  src.buffer = buf;
  const lp = audioCtx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = open ? 1200 : 700;
  const ng = audioCtx.createGain();
  envelope(ng, t0, 0.22, 0.001, 0.06);
  src.connect(lp).connect(ng).connect(out);
  src.start(t0);
  src.stop(t0 + 0.1);
  endClip(out, 0.25);
  return true;
}

/** Dev/e2e snapshot of the entryway audio state (drives `window.__duckEntry().audio`). */
export function entrywayAudioDebug(): { lastPlayed: 'doorbell' | 'knock' | null; playedCount: number } {
  return { lastPlayed, playedCount };
}
